import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import Button from "ui/Button";
import Callout from "ui/Callout";
import Loading from "ui/Loading";
import PageContent from "ui/PageContent";
import PreviewDiff from "features/Server/ServerGroupPreviewDiff";
import ServerGroupForm, {
  emptyTagRule,
  MatchRule,
  ruleRequiresKey,
} from "features/Server/ServerGroupForm";
import SettingsNav from "ui/SettingsNav";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import { useRoutes } from "utils/routes";

import { useCreateServerGroupMutation } from "features/Server/ServerGroupNew/gql/Mutation.create.generated";

const ServerGroupNew = () => {
  const currentOrganization = useCurrentOrganization();
  const navigate = useNavigate();
  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const { organizationServerGroupsSettings, serverGroupEdit } = useRoutes();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [matchRules, setMatchRules] = useState<MatchRule[]>([emptyTagRule()]);
  const [createServerGroup, { loading }] = useCreateServerGroupMutation();

  if (currentOrganization == null) {
    return <Loading error />;
  }

  if (!currentOrganization.enabledFeatures.serverGroupCrud) {
    return (
      <Navigate to={`/organizations/${currentOrganization.slug}`} replace />
    );
  }

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const saveDisabled = loading;

  const handleSave = () => {
    if (trimmedName === "") {
      setFlash("error", "Name is required.");
      return;
    }
    if (
      matchRules.some(
        (rule) =>
          (ruleRequiresKey(rule) && rule.key.trim() === "") ||
          rule.value.trim() === "",
      )
    ) {
      setFlash("error", "All match rule fields are required.");
      return;
    }
    createServerGroup({
      variables: {
        organizationSlug: currentOrganization.slug,
        name: trimmedName,
        description: trimmedDescription === "" ? null : trimmedDescription,
        matchRules: matchRules.map((rule) => ({
          matchType: rule.type,
          matchKey: ruleRequiresKey(rule) ? rule.key.trim() : null,
          pattern: rule.value.trim(),
        })),
      },
      refetchQueries: ["WithCurrentOrganization"],
      awaitRefetchQueries: true,
      onError: (error) => {
        setFlash("error", error.message);
      },
      onCompleted: (data) => {
        const newGroup = data.createServerGroup;
        setFlash("success", "Server group created");
        setNextFlashLocation(serverGroupEdit(newGroup.id));
        navigate(serverGroupEdit(newGroup.id));
      },
    });
  };

  const handleCancel = () => {
    navigate(organizationServerGroupsSettings(currentOrganization.slug));
  };

  return (
    <PageContent
      title="Create Server Group"
      pageCategory="servergroup"
      pageName="new"
      featureNav={<SettingsNav />}
    >
      {/* NOTE: This copy intentionally omits server group membership of groups/clusters. I will revisit it with the feature. */}
      <Callout className="mb-4" variant="info" title="What is a server group?">
        A server group is a collection of servers defined by match rules like
        tag or server name. Use it to view and manage part of your fleet as a
        single unit across pganalyze.
      </Callout>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <ServerGroupForm
            name={name}
            description={description}
            matchRules={matchRules}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onMatchRulesChange={setMatchRules}
          />
          <div className="flex items-center gap-2">
            <Button
              bare
              onClick={handleSave}
              className="btn btn-success"
              disabled={saveDisabled}
            >
              Create Server Group
            </Button>
            <Button bare onClick={handleCancel} variant="link">
              Cancel
            </Button>
          </div>
        </div>
        <div className="w-[390px] shrink-0">
          <PreviewDiff
            organizationSlug={currentOrganization.slug}
            matchRules={matchRules}
          />
        </div>
      </div>
    </PageContent>
  );
};

export default ServerGroupNew;
